<template>
  <div ref="hotelCard" class="hotels-info__hotel-card" :class="{ 'with-ihg': isIHG }">
    <!-- Offer Section -->
    <div v-if="showOfferBanner" class="hotels-info__offer">
      <component
        :is="ResponsiveModalOrSheet"
        :modal-id="`automatic-offer-${hotel.id}`"
        :sheet-id="`automatic-offer-${hotel.id}`"
      >
        <template #trigger>
          <button type="button" aria-haspopup="dialog" aria-expanded="false" class="hotels-info__offer-text">
            <span class="hotels-info__offer-text-lower">{{ hotel.offerInfo.automatic_crs_offer_label }}</span>
            <span class="s s-alert-info" aria-label="Information"></span>
          </button>
        </template>
        <template #content>
          <p>{{ i18n.hotelCard.discountInfo }}</p>
        </template>
      </component>
      <p class="hotels-info__offer-text" v-bind="offerTooltipAttributes">
        {{ offerText }}
      </p>
    </div>

    <!-- Images Section -->
    <div class="hotels-info__images utag-info-information-link">
      <button
        v-if="isDesktop"
        type="button"
        aria-haspopup="dialog"
        aria-expanded="false"
        class="hotels-info__quick-view-btn btn-hover t-link utag-info-information-link"
        :data-hotel-id="hotel.id"
        data-type="hotel"
        :data-lat="hotel.lat"
        :data-lng="hotel.lng"
        :data-zoom="hotel.gmaps_zoom"
        :data-name="hotel.title"
        :data-url="hotel.url"
        @click="openInformationModal($event, 'hotel_services', 'see hotel info', '', 'information')"
      >
        {{ i18n.hotelCard.quickView }}
      </button>
      <div class="swiper ib-swiper--template-default js-swiper-image-hotel-card">
        <div class="swiper-wrapper">
          <div v-for="(image, index) in hotel.images" :key="image.id" class="swiper-slide">
            <picture
              class="hotels-info__picture"
              :data-hotel-id="hotel.id"
              data-type="hotel"
              :data-lat="hotel.lat"
              :data-lng="hotel.lng"
              :data-zoom="hotel.gmaps_zoom"
              :data-name="hotel.title"
              :data-url="hotel.url"
              @click="handleImageClick"
            >
              <source
                :data-srcset="image.desktop.crops[0]"
                media="(min-width: 768px)"
                :srcset="image.desktop.crops[0]"
              />
              <source :data-srcset="image.mobile.crops[0]" :srcset="image.mobile.crops[0]" />
              <img :alt="image.alt" class="hotels-info__image" loading="lazy" />
            </picture>
            <div v-if="isLastMobileImage(index)" class="hotels-info__last-slide-cover">
              <div class="hotels-info__last-slide-button">
                <span class="s s-picture-gallery"></span>
                <span>{{ i18n.hotelCard.seePhotosSmall }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="swiper-button-prev btn-hover js-swiper-button-prev"></div>
        <div class="swiper-button-next btn-hover js-swiper-button-next"></div>
      </div>
      <img
        v-if="hotel.hotel_subsegmentation_logo"
        class="hotels-info__segmentation-logo"
        :src="hotel.hotel_subsegmentation_logo"
        loading="lazy"
      />
    </div>

    <!-- Main Information Section -->
    <div class="hotels-info__info">
      <div class="hotels-info__info-left">
        <!-- Badges -->
        <div class="hotels-info__badges">
          <div v-for="badge in visibleBadges" :key="badge.id">
            <component
              :is="ResponsiveModalOrSheet"
              :modal-id="`badge-${badge.id}`"
              :sheet-id="`badge-${badge.id}`"
              :title="badge.title"
              :description="badge.description"
              :link-text="badge.link_text"
              :target="badge.target"
              :href="badge.url"
            >
              <template #trigger>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded="false"
                  class="hotels-info__badge badge round uppercase-small"
                  :class="{ 'is-star-prestige utag-star-prestige-info': badge.is_star_prestige }"
                  v-bind="getBadgeDataAttributes(badge)"
                >
                  <span>{{ badge.title }}</span>
                </button>
              </template>
              <template #content>
                <div class="hotels-info__badge-modal">
                  <div class="hotels-info__badge badge round uppercase-small">{{ badge.title }}</div>
                  <div class="hotels-info__badge-description">{{ badge.description }}</div>
                  <a class="hotels-info__badge-link" :target="badge.target" :href="badge.url">{{ badge.link_text }}</a>
                </div>
              </template>
            </component>
          </div>

          <component
            :is="ResponsiveModalOrSheet"
            v-if="showMoreBadges"
            :modal-id="`more-badges-${hotel.id}`"
            :sheet-id="`more-badges-${hotel.id}`"
          >
            <template #trigger>
              <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded="false"
                class="hotels-info__badge badge round uppercase-small"
              >
                <span>+ {{ hiddenBadgesCount }}</span>
              </button>
            </template>
            <template #content>
              <div v-for="badge in hotel.hotel_badges" :key="badge.id" class="hotels-info__badge-modal">
                <div class="hotels-info__badge badge round uppercase-small">{{ badge.title }}</div>
                <div class="hotels-info__badge-description">{{ badge.description }}</div>
              </div>
            </template>
          </component>
        </div>

        <!-- Hotel Information -->
        <div class="hotels-info__hotel-info">
          <a class="hotels-info__hotel-name" :href="hotel.url">
            <div class="hotels-info__title t-highlight">
              {{ hotel.title }}
              <span class="hotels-info__stars">
                <span :class="`rating-stars rating-stars--primary r${hotel.hotel_category}`"></span>
              </span>
            </div>
          </a>
          <div class="hotels-info__city t-label">{{ hotel.city }}</div>
          <div v-if="hasTripadvisorRanking" class="hotels-info__ranking t-fb-label">
            {{ tripadvisorRating }}
          </div>
        </div>

        <!-- Ratings and Links -->
        <div class="hotels-info__ratings-links">
          <div v-if="showTripadvisorRatings" class="hotels-info__ratings">
            <img :src="hotel.tripadvisor?.rating?.src" :alt="hotel.tripadvisor?.rating?.alt" />
            <button
              type="button"
              aria-haspopup="dialog"
              aria-expanded="false"
              class="hotels-info__comments utag-see-hotel-card-reviews"
              :data-iframe-src="hotel.tripadvisor.comments.src"
              @click="openInformationModal($event, 'hotel_reviews', 'see opinions', 'see reviews', 'ratings')"
            >
              {{ hotel.tripadvisor.comments.title }}
            </button>
          </div>
        </div>

        <!-- Hotel Summary -->
        <div class="hotels-info__summary">
          <div class="hotels-info__summary-text" :class="{ 'is-truncable line-clamp-4': isMobile }">
            <span v-html="sanitizedSummary"></span>
          </div>
        </div>

        <a class="hotels-info__see-hotel-info t-link" :href="hotel.url">
          {{ i18n.hotelCard.viewHotel }}
        </a>
      </div>

      <!-- Prices and CTA Section -->
      <div class="hotels-info__info-right" :class="{ 'hotels-info__info-right--start': shouldStartInfoRight }">
        <div
          v-if="hasPriceReal"
          :class="`hotels-info__prices-info js-prices-info-${hotel.id}`"
          html="noindex true nofollow true"
          v-bind="priceDataAttributes"
        >
          <div v-if="shouldShowPriceBefore" class="price-cnt price-before hotels-info__price-before">
            <span class="prev-type">{{ i18n.fastbooking.before }} </span>
            <span
              class="prev-highlight"
              :data-curr-converter="hotel.price_real.base_price_value ? true : null"
              :data-currency="hotel.price_real.base_price_value ? hotel.price_real.currency : null"
              :data-price="hotel.price_real.base_price_value"
              v-html="sanitizedBasePriceWithCoin"
            ></span>
          </div>
          <div class="hotels-info__current-price">
            <span class="since-text"> {{ hotel.textBeforePrice }} </span>
            <span
              v-if="!i18n.hotelCard.hide_prices"
              class="highlight hotels-info__price js-real-price-info"
              :data-availability-lightbox="`#availability-dates-${hotel.id}`"
              v-html="sanitizedPriceWithCoin"
            >
            </span>
            <component
              :is="ResponsiveModalOrSheet"
              v-if="showTooltip"
              :modal-id="`discount-${hotel.id}`"
              :sheet-id="`discount-${hotel.id}`"
            >
              <template #trigger>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded="false"
                  class="s s-alert-info prince-info utag-discount-message"
                ></button>
              </template>
              <template #content>
                <p>
                  {{ hintCtaModalText }}
                </p>
              </template>
            </component>
          </div>
        </div>

        <!-- Hint Text -->
        <div v-if="hasPriceReal && hotel.card_text" class="hotels-info__hint-cnt js-hint-cnt">
          <div v-html="sanitizedCardText"></div>
        </div>

        <!-- CTA: Next Opening -->
        <div v-if="shouldShowNextOpening" class="call-to-action" :data-more-info="i18n.hotelCard.moreInfo">
          <div class="new-hotel">
            <div class="new-hotel-box">
              <span class="s s-calendar icon s-2x" aria-hidden="true"></span>
              <p class="ann">{{ i18n.hotelCard.nextOpeningAnnouncement }}</p>
              <p class="desc">
                {{ hotel.next_opening_date_text }}
              </p>
            </div>
            <a class="secondary-btn" :href="hotel.url">{{ i18n.hotelCard.visitHotel }}</a>
          </div>
        </div>
        <!-- CTA: Not Available -->
        <div v-else-if="shouldShowNotAvailable" class="call-to-action" :data-more-info="i18n.hotelCard.moreInfo">
          <div class="not-available-message">
            <ul>
              <li class="not-available">
                {{ i18n.hotelCard.notAvailableHotelMsg }}
              </li>
              <li class="phone">
                <a href="#phone-list" class="to-phones" data-call-center-link="true">
                  {{ i18n.hotelCard.notAvailableCallMsg }}
                </a>
              </li>
            </ul>
          </div>
          <a
            :href="hotel.availability"
            class="btn-floating-fastbooking btn-primary availability"
            :data-hotel-id="`h${hotel.id}`"
            :aria-label="`${i18n.hotelCard.checkAvailability} - ${hotel.title}`"
          >
            {{ i18n.hotelCard.checkAvailability }}
          </a>
        </div>
        <!-- CTA: Book Now (Bookings Page) -->
        <a
          v-else-if="shouldShowBookNowButton"
          class="hotels-info__cta-book-now btn-primary mt-2 s"
          html="noindex true nofollow true"
          v-bind="priceDataAttributes"
          :aria-label="`${i18n.fastbooking.bookNow} ${hotel.title}`"
        >
          {{ i18n.fastbooking.bookNow }}
        </a>
        <!-- CTA: Enjoy It Now (Other Pages) -->
        <button
          v-else-if="shouldShowEnjoyItNowButton"
          class="hotels-info__cta-book-now btn-primary mt-2"
          html="noindex true nofollow true"
          :aria-label="`${i18n.fastbooking.enjoyItNow} ${hotel.title}`"
          v-bind="priceDataAttributes"
          @click="handleSelectHotel(hotel)"
        >
          {{ i18n.fastbooking.enjoyItNow }}
        </button>
      </div>
    </div>
    <!-- IHG Disclaimer -->
    <div class="hotels-info__disclaimer">
      <template v-if="isIHG">
        <img class="img-logo" :src="i18n.hotelCard.ihgLogo" alt="ihg logo" />
        <span>
          {{ i18n.hotelCard.ihg }}
        </span>
      </template>
      <template v-else>
        <span>
          {{ i18n.hotelCard.noIhg }}
        </span>
      </template>
    </div>
  </div>
</template>

<script setup>
import 'swiper/css/navigation'

import dayjs from 'dayjs'
import DOMPurify from 'dompurify'
import Swiper from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'
import { computed, inject, onMounted, ref, watch } from 'vue'

import { initModal } from '../../../../../blocks/hotel_list/hotel_card_info_modal/hotel_card_info_modal'
import { currentDevice } from '../../../../../core/utils/currentDevice'
import MainHeader from '../../../../../layout/header/header'
import { FASTBOOKING_EVENTS } from '../../../../../shared/fastbooking/constants'
import fastbooking from '../../../../../shared/fastbooking/ibh_fastbooking'
import { replaceTranslationKeys as t } from '../../../../../utils/replaceTranslationKeys'
import { loadStyles } from '../../../mixins/LoadStyles/LoadStyles'
import BottomSheet from '../../BottomSheet/BottomSheet.vue'
import Modal from '../../Modal/Modal.vue'
import { emitUtagEvent } from '../HotelsInfo.analytics'

/**
 * Styles
 */
const COMPONENT_NAME = 'HotelCard'
loadStyles({ name: COMPONENT_NAME, folder: 'components/HotelsInfo' })

/**
 * Utils
 */

function getDefaultDate(primary, secondary, fallback) {
  if (primary) return dayjs(primary)
  if (secondary) return dayjs(secondary)
  return fallback instanceof dayjs ? fallback : dayjs()
}

// FIXME: Eliminar esto por favor, Esto es por el black friday, en principio es temporal
const showTooltip = false
/**
 * Props
 */
const props = defineProps({
  hotel: {
    type: Object,
    default: () => ({
      next_opening: false,
      offerInfo: null,
      showBanner: false,
      images: [],
      hotel_badges: [],
      tripadvisor_ranking_data: {},
      tripadvisor: {},
      price_real: null,
      card_text: '',
    }),
  },
  showLinks: {
    type: Boolean,
    default: false,
  },
})

/**
 * Refs
 */
const hotelCard = ref(null)
const dateFormat = ref('DD/MM/YYYY') // Initialize with a default, updated in onMounted

/**
 * Injects
 */
const i18n = inject('i18n')
const constrainedViewId = inject('constrainedViewId')

/**
 * Computed Properties to improve readability of conditions and logic.
 */

// Device
const isDesktop = computed(() => currentDevice.isDesktop)
const isMobile = computed(() => currentDevice.isMobile)
const ResponsiveModalOrSheet = computed(() => (isMobile.value ? BottomSheet : Modal))

// Offers
const showOfferBanner = computed(() => props.hotel.showBanner)
// offerStart
const offerStart = computed(() =>
  getDefaultDate(
    props.hotel.offerInfo?.offer_start,
    props.hotel.fastbookingDates?.check_in_date,
    dayjs() // Fallback: current date
  )
)

// offerEnd
const offerEnd = computed(() =>
  getDefaultDate(
    props.hotel.offerInfo?.offer_end,
    props.hotel.fastbookingDates?.check_out_date,
    offerStart.value.add(90, 'day') // Fallback: 90 days from calculated offerStart
  )
)

const offerText = computed(() => {
  let text = i18n.value.hotelCard.travelBetween
  if (props.hotel.offerInfo?.offer_start) {
    text = t(text, {
      stay_from: offerStart.value.format(dateFormat.value),
      stay_to: offerEnd.value.format(dateFormat.value),
    })
  } else {
    text = i18n.value.hotelCard.travelUntil
    text = t(text, {
      stay_to: offerEnd.value.format(dateFormat.value),
    })
  }
  return text
})

const offerTooltipText = computed(() => {
  let text = i18n.value.hotelCard.validStaysFromTo
  if (props.hotel.offerInfo?.offer_start) {
    text = t(text, {
      stay_from: offerStart.value.format(dateFormat.value),
      stay_to: offerEnd.value.format(dateFormat.value),
    })
  } else {
    text = i18n.value.hotelCard.validStaysTo
    text = t(text, {
      stay_to: offerEnd.value.format(dateFormat.value),
    })
  }
  return text
})

const offerTooltipAttributes = computed(() => ({
  'data-hotel_id': props.hotel.id, // Now dynamic, using hotel.id
  'data-stay_from': offerStart.value.format('YYYY/MM/DD'), // Now dynamic, using calculated offerStart
  'data-stay_to': offerEnd.value.format('YYYY/MM/DD'), // Now dynamic, using calculated offerEnd
  'data-tooltip': 'white',
  'data-desc': offerTooltipText.value,
  'data-position': 'bottom',
}))

// Images
const isLastMobileImage = index => isMobile.value && index === props.hotel.images.length // Original condition was `index == hotel.images.length`

// Badges
const showMoreBadges = computed(() => props.hotel.hotel_badges.length > 2)
const hiddenBadgesCount = computed(() => props.hotel.hotel_badges.length - 2)
const visibleBadges = computed(() => {
  if (props.hotel.hotel_badges.length > 2) return props.hotel.hotel_badges.slice(0, 2)
  return props.hotel.hotel_badges
})

const getBadgeDataAttributes = badge => {
  if (badge.is_star_prestige) {
    return {
      'data-hotel-id': props.hotel.id,
      'data-badge-id': badge.badge_id,
      'data-constrained-view-id': constrainedViewId.value,
    }
  }
  return {}
}

// Hotel Information
const isIHG = computed(() => (props.hotel.siteable ? props.hotel.siteable?.includes('beachfront') : false)) // TODO: REVIEW AUTOMATIC OFFER LIST -> hotel.siteable does not exist
const hasTripadvisorRanking = computed(
  () => props.hotel.tripadvisor_ranking_data.ranking && props.hotel.tripadvisor_ranking_data.geo_location_name
)
const tripadvisorRating = computed(() => {
  let text = i18n.value.hotelCard.tripadvisorRating
  text = text.replace('%{ranking}', props.hotel.tripadvisor_ranking_data.ranking)
  text = text.replace('%{place}', props.hotel.tripadvisor_ranking_data.geo_location_name)
  return text
})
const showTripadvisorRatings = computed(
  () => !props.hotel.next_opening && props.hotel.tripadvisor?.rating && props.hotel.tripadvisor?.comments
)

// Prices
const hasPriceReal = computed(
  () =>
    props.hotel.price_real &&
    Object.keys(props.hotel.price_real).length > 0 &&
    props.hotel.price_real.price_value !== null &&
    !i18n.value.hotelCard.hide_prices
)

const shouldShowPriceBefore = computed(
  () =>
    hasPriceReal.value &&
    Number(props.hotel.price_real.base_price_value) > 0 &&
    props.hotel.price_real.base_price_value !== props.hotel.price_real.price_value
)

const hintCtaModalText = computed(() => {
  let text = i18n.value.hotelCard.offerPriceDatesLegend
  text = text.replace('%{offer_start}', offerStart.value.format(dateFormat.value))
  text = text.replace('%{offer_end}', offerEnd.value.format(dateFormat.value))
  return text
})

const activMyIbeValue = ref(0) // Initialize as ref, update in onMounted or watch if it changes
const link_url = computed(() => {
  let link = props.hotel.fastbooking
  if (link && link.length) {
    link =
      link + (link.indexOf('?') !== -1 ? `&activeMiIB=${activMyIbeValue.value}` : `activeMiIB=${activMyIbeValue.value}`)
  }
  return link
})

const priceDataAttributes = computed(() => ({
  'data-curr-converter': 'inline',
  href: link_url.value,
  'data-price': props.hotel.price_real?.price_value,
  'data-base-price': props.hotel.price_real?.base_price_value,
  'data-resident-price': props.hotel.price_real?.resident_price_value,
  'data-currency': props.hotel.price_real?.currency,
  'data-promo-code': props.hotel.price_real?.promocode,
  'data-hotel-id': `h${props.hotel.id}`,
  'data-hotel-name': props.hotel.title,
}))

// Call to Action (CTA)
const shouldStartInfoRight = computed(() => props.hotel.next_opening || props.hotel.price_real === null)
const shouldShowNextOpening = computed(() => props.hotel.next_opening)
const isBookingsPage = computed(() => document.URL.includes('reservas') || document.URL.includes('bookings'))
const shouldShowNotAvailable = computed(
  () => !props.hotel.next_opening && (!hasPriceReal.value || props.hotel.price_real.price_value === null)
)
const shouldShowBookNowButton = computed(
  () => !shouldShowNextOpening.value && !shouldShowNotAvailable.value && isBookingsPage.value
)
const shouldShowEnjoyItNowButton = computed(
  () => !shouldShowNextOpening.value && !shouldShowNotAvailable.value && !isBookingsPage.value
)

/**
 * HTML Sanitization
 */
const sanitizedSummary = computed(() => DOMPurify.sanitize(props.hotel.summary))
const sanitizedBasePriceWithCoin = computed(() =>
  DOMPurify.sanitize(props.hotel.price_real?.base_price_with_coin || '')
)
const sanitizedPriceWithCoin = computed(() => DOMPurify.sanitize(props.hotel.price_real?.price_with_coin || ''))
const sanitizedCardText = computed(() =>
  DOMPurify.sanitize(`${props.hotel.card_text} ${i18n.value.hotelCard.taxesIncluded}`)
)

/**
 * Methods
 */
function initSwiper() {
  Swiper.use([Pagination, Navigation])

  const block = hotelCard.value
  if (!block) return

  const swiperDefault = block.querySelector('.js-swiper-image-hotel-card')

  if (!swiperDefault) return

  const swiperDefaultNavigationNext = swiperDefault.querySelector(':scope > .js-swiper-button-next')
  const swiperDefaultNavigationPrev = swiperDefault.querySelector(':scope > .js-swiper-button-prev')

  new Swiper(swiperDefault, {
    slidesPerView: 1,
    spaceBetween: 0,
    loop: false,
    nested: true,
    navigation: {
      nextEl: swiperDefaultNavigationNext,
      prevEl: swiperDefaultNavigationPrev,
    },
    lazyPreloadPrevNext: 1,
  })
}

const infoModalContent = hotelId => {
  const priceContainer = document.querySelector(`.js-prices-info-${hotelId}`)
  if (priceContainer) {
    const realPrice = priceContainer.querySelector('.js-real-price-info')
    return {
      hotelPrice: props.hotel.price_real,
      hotelCardText: props.hotel.card_text,
      hotelRealPrice: realPrice ? realPrice.innerHTML : '',
      linkUrl: priceContainer.getAttribute('href'),
    }
  }
  return {}
}

const openInformationModal = (event, event_name, event_act, event_lbl, contentToShow) => {
  emitUtagEvent({
    event_name,
    event_cat: 'hotel information',
    event_act,
    event_lbl,
    event_purpose: 'inspiration',
    event_structure: 'body',
  })
  initModal({
    element: event.target,
    hotelId: props.hotel.id,
    infoModalContent: infoModalContent(props.hotel.id),
    contentToShow,
  })
}

const handleImageClick = event => {
  if (!isDesktop.value) {
    openInformationModal(event, 'hotel_services', 'see hotel info', '', '')
  }
}

function handleSelectHotel(hotel) {
  emitUtagEvent({
    event_name: 'searcher_hotel',
    event_cat: 'searcher',
    event_act: 'dates',
    event_lbl: '',
    event_purpose: 'booking',
    event_structure: 'body',
    checkOnce: false,
  })
  fastbooking.setDestination(`h${hotel.id}`)

  fastbooking.on(
    FASTBOOKING_EVENTS.DATES_CHANGED,
    () => {
      if (fastbooking.state.place_to_go.id_and_type === `h${hotel.id}` && hotel.price_real?.promocode) {
        fastbooking.setPromocode(hotel.price_real.promocode)
      }
    },
    {
      once: true,
    }
  )

  if (!isMobile.value) {
    MainHeader.openPanel()
  } else {
    fastbooking.openStickyFastbooking()
  }

  fastbooking.openDates()
}

/**
 * Lifecycle Hooks
 */
onMounted(() => {
  dateFormat.value = document.documentElement.dataset.dateFormat || 'DD/MM/YYYY'
  initSwiper()
  // Update activMyIbeValue after DOM is ready
  activMyIbeValue.value = $('.js-myibe-toggle').hasClass('active') ? 1 : 0
  IB.currencyForm.forceUpdate($(hotelCard.value))
})

/**
 * Watchers
 */
watch(
  () => props.hotel.price_real?.price_with_coin,
  () => {
    // Ensure hotelCard.value exists before calling forceUpdate
    if (hotelCard.value) {
      IB.currencyForm.forceUpdate($(hotelCard.value))
    }
  },
  { flush: 'post' }
)
</script>
